import React, { useState, useEffect, useRef } from "react";
import { Input, AutoComplete, Button, message } from "antd";
import { controller } from "../controller";

// Committed static guide in public/.
const GUIDE_URL = "/Marketplace_Registration_and_Installation_Guide.pdf";

const SikkaSetup = (props) => {
  const [offices, setOffices] = useState([]); // [{id, name, sikkaId, connected}]
  const [loading, setLoading] = useState(false);
  // Sikka practices available to claim (GET /clinics/list-pms-offices/), shared
  // across every office row. Lazy-loaded once, the first time a field is focused.
  const [pmsList, setPmsList] = useState([]);
  const pmsLoadedRef = useRef(false);

  useEffect(() => {
    let stored = [];
    try {
      stored = JSON.parse(localStorage.getItem("office_ids")) || [];
    } catch (e) {
      stored = [];
    }
    const list = (stored || []).map((o) => ({
      id: o.office_id || o.id,
      name: o.office_name || o.name || `Office ${o.office_id || o.id}`,
      sikkaId: "",
      connected: false,
    }));
    setOffices(list);
    // Best-effort: flag offices already connected so we don't re-prompt them.
    list.forEach((o, idx) => {
      controller
        .getOfficeSikkaStatus(o.id)
        .then((res) => {
          if (res && res.sikka_connected) {
            setOffices((prev) =>
              prev.map((p, i) =>
                i === idx ? { ...p, connected: true, sikkaId: res.sikka_office_id || "" } : p
              )
            );
          }
        })
        .catch(() => {});
    });
  }, []);

  // Fetch the claimable-practices list once, on first field focus. Best-effort:
  // on failure the field still works as a plain text box for a typed Sikka ID.
  const ensurePmsList = async () => {
    if (pmsLoadedRef.current) return;
    pmsLoadedRef.current = true;
    try {
      const res = await controller.getPMSData();
      if (Array.isArray(res)) setPmsList(res);
    } catch (e) {
      /* leave suggestions empty */
    }
  };

  // Suggestions shared by every office row. `value` is the Sikka office id that
  // fills the field; `searchText` lets users match by practice name OR id.
  const pmsOptions = pmsList.map((p) => ({
    value: String(p.office_id),
    label: `${p.practice_name || "Unknown practice"} — ${[p.city, p.state]
      .filter(Boolean)
      .join(", ")} (${p.office_id})`,
    searchText: `${p.office_id} ${p.practice_name || ""} ${p.city || ""} ${p.state || ""}`.toLowerCase(),
  }));

  const setSikkaId = (idx, val) =>
    setOffices((prev) => prev.map((o, i) => (i === idx ? { ...o, sikkaId: val } : o)));

  const handleSubmit = async () => {
    const payload = offices
      .filter((o) => !o.connected && (o.sikkaId || "").trim())
      .map((o) => ({ office_id: o.id, sikka_id: o.sikkaId.trim() }));
    if (payload.length === 0) {
      message.warning("Enter an integration ID for at least one office, or choose 'Set up later'.");
      return;
    }
    setLoading(true);
    const res = await controller.setOfficeSikka(payload, false);
    setLoading(false);
    if (res.status < 250) {
      // The backend asks the sync worker to start this office's first sync right
      // away; `sync_requested` says whether the worker took it. If not, the
      // nightly run picks the office up — nothing for the user to do.
      const syncing = Object.values(res.sync_requested || {}).some(Boolean);
      message.success(
        syncing
          ? "PMS integration connected — syncing your practice data now. Patients will appear over the next few minutes."
          : "PMS integration connected"
      );
      props.readOnboardingStatus();
    } else {
      const err =
        (res.errors && res.errors[0] && res.errors[0].error) ||
        res.detail ||
        "Couldn't connect the PMS integration";
      message.error(err);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    const res = await controller.setOfficeSikka([], true);
    setLoading(false);
    if (res.status < 250) props.readOnboardingStatus();
    else message.error("Could not skip. Please try again.");
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Connect your PMS</p>
      <p style={{ color: "#555", marginBottom: 16 }}>
        Follow the guide to register for the PMS integration and install its utility on the computer
        that runs your PMS, then find your office below — or enter its integration ID.
      </p>
      <div style={{ marginBottom: 20 }}>
        <a
          href={GUIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#6B43B5", fontWeight: 600, textDecoration: "underline" }}
        >
          Download the PMS integration setup guide (PDF)
        </a>
      </div>
      {offices.map((o, idx) => (
        <div key={o.id} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{o.name}</div>
          {o.connected ? (
            <div style={{ color: "#2e7d32" }}>
              ✓ Connected{o.sikkaId ? ` (${o.sikkaId})` : ""}
            </div>
          ) : (
            <AutoComplete
              value={o.sikkaId}
              options={pmsOptions}
              onFocus={ensurePmsList}
              onChange={(val) => setSikkaId(idx, val)}
              onSelect={(val) => setSikkaId(idx, val)}
              filterOption={(input, option) =>
                (option && option.searchText ? option.searchText : "").indexOf(
                  (input || "").toLowerCase()
                ) !== -1
              }
              style={{ width: "100%" }}
            >
              <Input
                placeholder="Search your practice or enter the office's integration ID"
                style={{ height: 39 }}
              />
            </AutoComplete>
          )}
        </div>
      ))}
      <Button
        onClick={handleSubmit}
        loading={loading}
        className="login-button"
        style={{ marginTop: 16, marginBottom: 12 }}
      >
        Connect PMS integration
      </Button>
      <div className="skip-btn mt5" style={{ cursor: "pointer" }} onClick={handleSkip}>
        Set up later
      </div>
    </div>
  );
};

export default SikkaSetup;
